import { Component, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ICellRendererAngularComp } from "@bfx/ag-grid-angular";
import {
  BmarkBaseRiskDisplayModel,
  BmarkRiskTGDisplayModel,
} from "shared/cubiq-services/model/benchmarks/BmarkSpotRiskDisplay.model";
import { ICellRendererParams } from "ag-grid-community/main";

const ONE_MILLION: CubiqTS.BigNumber = new CubiqTS.BigNumber(1000000);

const METALS_DEFAULT_DISPLAY_MILLIONS = false; // metals are NOTIONAL
@Component({
  selector: "spot-risk-trader-group",
  templateUrl: "./bmark-spot-risk-trader-group.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BmarkSpotRiskTraderGroupRendererComponent implements ICellRendererAngularComp {
  /// DATA
  public params: any;
  public cell: any;
  //
  public tgId: CubiqTS.Long = null;
  public precision: number = 2;
  public materialSuffix: boolean = false;
  public displayInMillions: boolean = true;
  public displayEmpty: boolean = false;
  public totalAmtText: string = null;
  public fillAmtText: string = null;
  //
  public isNegative: boolean = false;
  public isZero: boolean = false;
  public hasActiveOrders: boolean = true;
  public isFullyFilled: boolean = false;

  /**
   * BmarkSpotRiskTraderGroupRendererComponent is immutable after agInit() due to refresh() returning false
   *
   * Change from ag-grid will destroy component and create a new one
   *
   * A ChangeDetectorRef push (markForCheck) must never be called
   */
  constructor() {}

  public agInit(params: ICellRendererParams): void {
    this.params = params;
    this.cell = { row: params.value, col: params.colDef.headerName };
    this.processParams(params);
  }

  public refresh(): boolean {
    return false;
  }

  protected processParams(params: any): void {
    if (params != null && params.data != null) {
      const displayModel = params.data as BmarkBaseRiskDisplayModel;
      this.tgId = params.tgId;
      this.displayEmpty = typeof params.displayEmpty === "boolean" ? params.displayEmpty : false;
      this.isZero = true;
      //
      this.precision = typeof params.data.precision === "number" ? params.data.precision : 0;
      this.materialSuffix =
        typeof params.data.isMaterial === "boolean" ? params.data.isMaterial : false;
      this.displayInMillions = params.data.isMaterial
        ? METALS_DEFAULT_DISPLAY_MILLIONS
        : params.data.notation in CubiqTS.PositionUnit &&
          params.data.notation == CubiqTS.PositionUnit.Million;
      ///
      if (
        this.tgId instanceof CubiqTS.Long &&
        params != null &&
        Array.isArray(displayModel.tgData) &&
        displayModel.tgData.length > 0
      ) {
        let tgData = displayModel.tgData.find((item) =>
          (item.tgId as CubiqTS.Long).equals(this.tgId),
        );
        if (tgData instanceof BmarkRiskTGDisplayModel) {
          //open amt
          if (tgData.totalAmount instanceof CubiqTS.BigNumber) {
            this.hasActiveOrders = tgData.hasActiveOrders;
            this.isZero = tgData.totalAmount.isEqualTo(0) && tgData[params.orderCountProperty] == 0;
            this.totalAmtText = this.formatAmt(tgData.totalAmount, !this.isZero, params);
            this.isNegative = tgData.totalAmount.isLessThan(0);
          }
          // fill amt (only needed for external tg)
          if (tgData.isBmarkExternal) {
            if (tgData.fillAmount instanceof CubiqTS.BigNumber) {
              this.fillAmtText = this.formatAmt(tgData.fillAmount, true, params);
            }
            this.isFullyFilled = tgData.fillAmount
              .absoluteValue()
              .isEqualTo(tgData.totalAmount.absoluteValue());
          }
        }
      }
    } else {
      this.totalAmtText = "";
      this.fillAmtText = "";
      this.isNegative = false;
      this.isZero = true;
    }
  }

  public formatAmt(value: CubiqTS.BigNumber, displayZero: boolean, params: any): string {
    let ret: string = displayZero ? new CubiqTS.BigNumber(0).toFormat(this.precision) : "";
    if (value instanceof CubiqTS.BigNumber && !value.isZero()) {
      if (this.displayInMillions) {
        // Millions
        ret = value.dividedBy(ONE_MILLION).toFormat(this.precision);
      } else {
        // Notional Amount
        ret = value.toFormat(this.precision);
      }
    }
    return ret;
  }
}
